/**  @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Alert,
  Button,
  Cell,
  FormLabel,
  Grid,
  Heading,
  HFlow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  VFlow,
} from 'bold-ui'
import { Form, FormPrompt, FormRenderProps, SubmitButton, TextAreaField } from 'components/form'
import { useFlags } from 'config/useFlagsContext'
import { ReceitaMedicamento, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { Redirect, useRouteMatch } from 'react-router'
import { useHistory } from 'react-router-dom'
import { isUndefinedOrNull } from 'util/checks'
import { formatDate } from 'util/date/formatDate'
import { metaPath } from 'util/metaPath'
import { createValidator, maxLength, required } from 'util/validation'

export interface MedicamentoInterromperFormModel {
  id: ID
  justificativa: string
  usoContinuoGrupoId: ID
}

export interface InterromperTratamentoModalProps {
  receita: ReceitaMedicamento
  onSubmit(medicamentos: MedicamentoInterromperFormModel): void
  onClear(): void
}

const path = metaPath<MedicamentoInterromperFormModel>()

export const validate = createValidator<MedicamentoInterromperFormModel>({
  id: [required],
  justificativa: [required, maxLength(200)],
})

export default function InterromperTratamentoModal(props: InterromperTratamentoModalProps) {
  const { receita, onSubmit, onClear } = props
  const history = useHistory()
  const match = useRouteMatch()

  const { RENOVACAO_PRESCRICAO_ENABLED } = useFlags()

  if (!receita) {
    return <Redirect to='/403' />
  }

  const goBack = () => {
    history.push(match.url.replace('/interromper-tratamento', ''))
  }

  const handleOnSubmitSucceeded = () => {
    history.push(match.url.replace('/interromper-tratamento', ''))
    onClear()
  }

  const renderForm = (renderProps: FormRenderProps<MedicamentoInterromperFormModel>) => {
    const wasRenovado = RENOVACAO_PRESCRICAO_ENABLED && !isUndefinedOrNull(receita.receitaUsoContinuoGrupo)

    const dataInicioTratamento = formatDate(
      wasRenovado ? receita.receitaUsoContinuoGrupo.dtInicioTratamento : receita.dtInicioTratamento
    )

    return (
      <Fragment>
        <ModalHeader
          title={`Deseja interromper o tratamento${wasRenovado ? ' do medicamento de uso contínuo' : ''}?`}
          icon={{ name: 'exclamationTriangleOutline', fill: 'danger' }}
        />
        <ModalBody>
          <VFlow>
            <FormPrompt />
            <Grid>
              {wasRenovado ? (
                <Cell size={12}>
                  <Alert inline type='warning'>
                    Este medicamento é de uso contínuo e teve sua prescrição renovada. Ao interromper o tratamento, ele
                    será interrompido em sua totalidade e todas as prescrições relacionadas também terão seus
                    tratamentos interrompidos.
                  </Alert>
                </Cell>
              ) : null}
              <Cell size={6}>
                <VFlow
                  style={css`
                    word-break: break-all;
                  `}
                  vSpacing={0}
                >
                  <Text data-cy='InterromperTratamento.PrincipioAtivo' fontWeight='bold'>
                    {`${receita.medicamento.principioAtivo} ${receita.medicamento.concentracao ?? ''}`.trim()}
                  </Text>
                  <Text data-cy='InterromperTratamento.Posologia'>{receita.posologia}</Text>
                </VFlow>
              </Cell>
              <Cell size={6}>
                <VFlow vSpacing={0}>
                  <Text fontWeight='bold'>Período</Text>
                  <Text data-cy='InterromperTratamento.Periodo'>
                    {`${dataInicioTratamento} - ${
                      receita.medidaTempoTratamento !== UnidadeMedidaTempoEnum.INDETERMINADO
                        ? formatDate(receita.dtFimTratamento)
                        : 'Indeterminado'
                    }${receita.usoContinuo ? ' | Uso contínuo' : ''}`}
                  </Text>
                </VFlow>
              </Cell>
              <Cell size={12}>
                <VFlow vSpacing={0}>
                  <Heading level={5}>Recomendações</Heading>
                  {receita.recomendacoes ? (
                    <Text
                      style={css`
                        word-break: break-all;
                      `}
                      data-cy='InterromperTratamento.Recomendacoes'
                    >
                      {receita.recomendacoes}
                    </Text>
                  ) : (
                    <Text fontStyle='italic' data-cy='InterromperTratamento.Recomendacoes'>
                      Não há recomendações para o medicamento
                    </Text>
                  )}
                </VFlow>
              </Cell>
              <Cell size={12}>
                <VFlow vSpacing={0.5} data-cy='InterromperTratamento.Justificativa'>
                  <FormLabel label='Justificativa' required />
                  <TextAreaField
                    name={path.justificativa}
                    style={css`
                      resize: none;
                    `}
                    maxLength={200}
                    data-cy='InterromperTratamento.JustificativaTextArea'
                  />
                </VFlow>
              </Cell>
            </Grid>
          </VFlow>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button kind='normal' size='medium' onClick={goBack}>
              Cancelar
            </Button>
            <SubmitButton
              handleSubmit={renderProps.handleSubmit}
              kind='primary'
              skin='default'
              size='medium'
              data-cy='InterromperTratamento.ConfirmarButton'
            >
              Confirmar
            </SubmitButton>
          </HFlow>
        </ModalFooter>
      </Fragment>
    )
  }

  return (
    <Modal
      open={true}
      closeOnBackdropClick={false}
      depthLevel={2}
      manageOverflow={false}
      onClose={goBack}
      style={css`
        width: 40rem;
      `}
    >
      <Form<MedicamentoInterromperFormModel>
        onSubmit={onSubmit}
        onSubmitSucceeded={handleOnSubmitSucceeded}
        subscription={{ values: true, pristine: true, error: true }}
        render={renderForm}
        validate={validate}
        initialValues={{
          id: receita.id,
          justificativa: undefined,
          usoContinuoGrupoId: receita.receitaUsoContinuoGrupo?.id,
        }}
      />
    </Modal>
  )
}
